import { type ChainAvailabilityItem } from '@/models/appointment-chain-availability.ts';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
    type ChainAutoSuggestionParams,
    getChainAutoSuggestion,
} from '@/service/Appointments/chain-availability-service.ts';

import { useBookingStore } from '@/store.js';

interface UseChainAvailabilityResult {
    fetchAutoSuggestion: (params: ChainAutoSuggestionParams) => Promise<ChainAvailabilityItem[] | undefined>;
}

/**
 * Wraps the auto-suggest service: flips `isChainSuggestionFetching` and turns a thrown error into a
 * toast + `undefined`, so callers branch on the return value instead of `try/catch`. No react-query —
 * the result is paged through `useSearchStore`, not cached by key.
 */
export const useChainAvailability = (): UseChainAvailabilityResult => {
    const { t } = useTranslation();

    const fetchAutoSuggestion = async (
        params: ChainAutoSuggestionParams
    ): Promise<ChainAvailabilityItem[] | undefined> => {
        useBookingStore.setState({ isChainSuggestionFetching: true });
        try {
            return await getChainAutoSuggestion(params);
        } catch (error) {
            console.error('Chain auto-suggestion fetch failed:', error);
            toast.error(t('ChainAvailabilityError'));
            return undefined;
        } finally {
            useBookingStore.setState({ isChainSuggestionFetching: false });
        }
    };

    return { fetchAutoSuggestion };
};
